import React, { useState, useRef, useEffect } from 'react';

// Price options (BHD) - from 4 BD to 25 BD with 0.5 increments
const priceOptions = [];
for (let price = 4; price <= 25; price += 0.5) {
  // Format to show .5 as .5 and whole numbers without .0
  const formattedPrice = price % 1 === 0 ? price : price.toFixed(1);
  priceOptions.push(`${formattedPrice} BD`);
}

// Bahrain Banks
const BAHRAIN_BANKS = [
  'Ahli United Bank',
  'Al Baraka Islamic Bank',
  'Al Salam Bank',
  'Bahrain Development Bank',
  'Bahrain Islamic Bank (BisB)',
  'Bank of Bahrain and Kuwait (BBK)',
  'HSBC Bank Middle East',
  'Khaleeji Bank',
  'Kuwait Finance House (Bahrain)',
  'National Bank of Bahrain (NBB)',
  'Other'
];

const BAHRAIN_BANKS_AR = [
  'بنك الأهلي المتحد',
  'بنك البركة الإسلامي',
  'بنك السلام',
  'بنك البحرين للتنمية',
  'بنك البحرين الإسلامي',
  'بنك البحرين والكويت',
  'بنك إتش إس بي سي الشرق الأوسط',
  'بنك الخليجي',
  'بيت التمويل الكويتي (البحرين)',
  'البنك الوطني البحريني',
  'أخرى'
];

// Custom Dropdown Component
const CustomDropdown = ({ name, value, onChange, options, placeholder, disabled = false, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = value ? options.find(opt => opt.value === value)?.label || value : placeholder;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 16px',
          border: `2px solid ${error ? '#dc2626' : (isOpen ? '#ff7a1a' : '#e5e7eb')}`,
          borderRadius: '12px',
          background: disabled ? '#f9fafb' : 'white',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '15px',
          color: value ? '#1f2937' : '#9ca3af',
          minHeight: '44px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = '#d1d5db';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = error ? '#dc2626' : '#e5e7eb';
          }
        }}
      >
        <span>{displayText}</span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke={isOpen ? '#ff7a1a' : '#6b7280'} 
          strokeWidth="2"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            maxHeight: '220px',
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          {options.map((option) => {
            const isSelected = value === option.value;
            return (
              <div
                key={option.value}
                onClick={() => {
                  onChange({ target: { name, value: option.value } });
                  setIsOpen(false);
                }}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: isSelected ? '#fff4e6' : 'transparent',
                  color: isSelected ? '#ff7a1a' : '#1f2937',
                  fontWeight: isSelected ? '600' : '400',
                  fontSize: '15px',
                  transition: 'all 0.15s',
                  marginBottom: '4px'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#1f2937';
                  }
                }}
              >
                {option.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function Step7Pricing({ wizardData, onNext, onBack, onSave, translations, isArabic, language }) {
  const t = translations || {};
  const lessonFormatPreference = wizardData.lesson_format_preference || null;
  
  // Determine which pricing fields to show based on lesson format
  const isBoth = lessonFormatPreference === 'both';
  const isOnlineOnly = lessonFormatPreference === 'online_only';
  const isFaceToFaceOnly = lessonFormatPreference === 'face_to_face_only';
  
  // Pricing states - support both separate (online/face-to-face) and combined pricing
  const [pricingPrivate, setPricingPrivate] = useState(wizardData.pricing_private_per_hour || '');
  const [pricingGroup, setPricingGroup] = useState(wizardData.pricing_group_per_hour || '');
  
  // Separate pricing for "both" option
  const [pricingPrivateOnline, setPricingPrivateOnline] = useState(wizardData.pricing_private_online_per_hour || '');
  const [pricingGroupOnline, setPricingGroupOnline] = useState(wizardData.pricing_group_online_per_hour || '');
  const [pricingPrivateFaceToFace, setPricingPrivateFaceToFace] = useState(wizardData.pricing_private_face_to_face_per_hour || '');
  const [pricingGroupFaceToFace, setPricingGroupFaceToFace] = useState(wizardData.pricing_group_face_to_face_per_hour || '');
  
  const [groupPricingModel, setGroupPricingModel] = useState(wizardData.group_pricing_model || 'per_person');
  const [groupPricingModelOnline, setGroupPricingModelOnline] = useState(wizardData.group_pricing_model_online || 'per_person');
  const [groupPricingModelFaceToFace, setGroupPricingModelFaceToFace] = useState(wizardData.group_pricing_model_face_to_face || 'per_person');
  
  const [iban, setIban] = useState(wizardData.iban || '');
  const [bankName, setBankName] = useState(wizardData.bank_name || '');
  const [otherBankName, setOtherBankName] = useState(wizardData.bank_name && !BAHRAIN_BANKS.includes(wizardData.bank_name) && !BAHRAIN_BANKS_AR.includes(wizardData.bank_name) ? wizardData.bank_name : '');
  const [accountHolderName, setAccountHolderName] = useState(wizardData.account_holder_name || '');
  const [errors, setErrors] = useState({});

  // Prepare bank options
  const bankOptions = (isArabic ? BAHRAIN_BANKS_AR : BAHRAIN_BANKS).map((bank, index) => ({
    value: isArabic ? BAHRAIN_BANKS[index] : bank,
    label: bank
  }));

  // Prepare price options for dropdown
  const priceDropdownOptions = priceOptions.map(price => ({
    value: price,
    label: price
  }));

  // Group pricing model options
  const groupPricingModelOptions = [
    { value: 'per_person', label: isArabic ? 'لكل طالب' : 'Per Student' },
    { value: 'fixed', label: isArabic ? 'سعر ثابت' : 'Fixed Price' }
  ];

  const validate = () => {
    const newErrors = {};
    
    if (isBoth) {
      // Validate both online and face-to-face pricing
      if (!pricingPrivateOnline || pricingPrivateOnline === '') {
        newErrors.pricingPrivateOnline = isArabic ? 'سعر الدرس الخاص (أونلاين) مطلوب' : 'Private lesson price (Online) is required';
      }
      if (!pricingGroupOnline || pricingGroupOnline === '') {
        newErrors.pricingGroupOnline = isArabic ? 'سعر الدرس الجماعي (أونلاين) مطلوب' : 'Group lesson price (Online) is required';
      }
      if (!pricingPrivateFaceToFace || pricingPrivateFaceToFace === '') {
        newErrors.pricingPrivateFaceToFace = isArabic ? 'سعر الدرس الخاص (حضوري) مطلوب' : 'Private lesson price (Face-to-Face) is required';
      }
      if (!pricingGroupFaceToFace || pricingGroupFaceToFace === '') {
        newErrors.pricingGroupFaceToFace = isArabic ? 'سعر الدرس الجماعي (حضوري) مطلوب' : 'Group lesson price (Face-to-Face) is required';
      }
    } else {
      // Validate single pricing (online only or face-to-face only)
      if (!pricingPrivate || pricingPrivate === '') {
        newErrors.pricingPrivate = isArabic ? `${t.perHourPrivate} ${t.required}` : `${t.perHourPrivate} ${t.required}`;
      }
      if (!pricingGroup || pricingGroup === '') {
        newErrors.pricingGroup = isArabic ? `${t.perHourGroup} ${t.required}` : `${t.perHourGroup} ${t.required}`;
      }
    }

    // Banking Information is now required
    if (!iban || iban.trim() === '') {
      newErrors.iban = isArabic ? `${t.iban || 'IBAN'} ${t.required}` : `${t.iban || 'IBAN'} ${t.required}`;
    }

    if (!bankName || bankName === '') {
      newErrors.bankName = isArabic ? `${t.bankName || 'Bank Name'} ${t.required}` : `${t.bankName || 'Bank Name'} ${t.required}`;
    } else if (bankName === 'Other' || bankName === 'أخرى') {
      // If "Other" is selected, require the custom bank name
      if (!otherBankName || otherBankName.trim() === '') {
        newErrors.otherBankName = isArabic ? `${t.bankName || 'Bank Name'} ${t.required}` : `${t.bankName || 'Bank Name'} ${t.required}`;
      }
    }

    if (!accountHolderName || accountHolderName.trim() === '') {
      newErrors.accountHolderName = isArabic ? `${t.accountHolderName || 'Account Holder Name'} ${t.required}` : `${t.accountHolderName || 'Account Holder Name'} ${t.required}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    // Use otherBankName if "Other" is selected, otherwise use bankName
    const finalBankName = (bankName === 'Other' || bankName === 'أخرى') ? otherBankName : bankName;

    const pricingData = {
      // For "both" option, store separate pricing
      pricing_private_per_hour: isBoth ? null : pricingPrivate,
      pricing_group_per_hour: isBoth ? null : pricingGroup,
      pricing_private_online_per_hour: isBoth ? pricingPrivateOnline : null,
      pricing_group_online_per_hour: isBoth ? pricingGroupOnline : null,
      pricing_private_face_to_face_per_hour: isBoth ? pricingPrivateFaceToFace : null,
      pricing_group_face_to_face_per_hour: isBoth ? pricingGroupFaceToFace : null,
      
      // Group pricing models
      group_pricing_model: isBoth ? null : groupPricingModel,
      group_pricing_model_online: isBoth ? groupPricingModelOnline : null,
      group_pricing_model_face_to_face: isBoth ? groupPricingModelFaceToFace : null,
      
      iban: iban,
      bank_name: finalBankName,
      account_holder_name: accountHolderName
    };

    await onSave(pricingData);
    onNext(pricingData);
  };

  return (
    <div>
      <h2 className="step-title">Pricing</h2>
      <p className="step-subtitle">
        Set your hourly rates. You can edit pricing later from your profile.
      </p>

      <form className="wizard-form" onSubmit={handleSubmit}>
        {isBoth ? (
          <>
            {/* Online Pricing Section */}
            <div style={{ 
              marginBottom: '32px', 
              paddingBottom: '24px', 
              borderBottom: '1px solid #e5e7eb' 
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
                {isArabic ? 'التسعير (أونلاين)' : 'Online Pricing'}
              </h3>
              
              {/* Online Private Lesson Price */}
              <div className="form-group">
                <label>{t.perHourPrivate || 'Per hour (private)'} *</label>
                <CustomDropdown
                  name="pricing_private_online"
                  value={pricingPrivateOnline}
                  onChange={(e) => {
                    setPricingPrivateOnline(e.target.value);
                    if (errors.pricingPrivateOnline) {
                      setErrors(prev => ({ ...prev, pricingPrivateOnline: null }));
                    }
                  }}
                  options={priceDropdownOptions}
                  placeholder={isArabic ? 'اختر السعر' : 'Select price'}
                  error={errors.pricingPrivateOnline}
                />
                {errors.pricingPrivateOnline && <span className="error-message">{errors.pricingPrivateOnline}</span>}
              </div>

              {/* Online Group Lesson Price */}
              <div className="form-group">
                <label>{t.perHourGroup || 'Per hour (group)'} *</label>
                <CustomDropdown
                  name="pricing_group_online"
                  value={pricingGroupOnline}
                  onChange={(e) => {
                    setPricingGroupOnline(e.target.value);
                    if (errors.pricingGroupOnline) {
                      setErrors(prev => ({ ...prev, pricingGroupOnline: null }));
                    }
                  }}
                  options={priceDropdownOptions}
                  placeholder={isArabic ? 'اختر السعر' : 'Select price'}
                  error={errors.pricingGroupOnline}
                />
                {errors.pricingGroupOnline && <span className="error-message">{errors.pricingGroupOnline}</span>}
                
                {/* Group Pricing Model - Online */}
                {pricingGroupOnline && (
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ 
                      fontSize: '16px', 
                      fontWeight: 600, 
                      color: '#1f2937', 
                      marginBottom: '12px', 
                      display: 'block' 
                    }}>
                      {isArabic ? 'نموذج تسعير المجموعة (أونلاين)' : 'Group Pricing Model (Online)'} *
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px',
                      flexDirection: isArabic ? 'row-reverse' : 'row'
                    }}>
                      {groupPricingModelOptions.map((option) => {
                        const isSelected = groupPricingModelOnline === option.value;
                        return (
                          <div
                            key={option.value}
                            onClick={() => setGroupPricingModelOnline(option.value)}
                            style={{
                              flex: 1,
                              padding: '16px',
                              border: `2px solid ${isSelected ? '#ff7a1a' : '#e5e7eb'}`,
                              borderRadius: '12px',
                              background: isSelected ? '#fff4e6' : 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              textAlign: 'center'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#d1d5db';
                                e.currentTarget.style.background = '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.background = 'white';
                              }
                            }}
                          >
                            <div style={{
                              fontSize: '15px',
                              fontWeight: isSelected ? '600' : '500',
                              color: isSelected ? '#ff7a1a' : '#1f2937',
                              marginBottom: '4px'
                            }}>
                              {option.label}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              lineHeight: '1.4'
                            }}>
                              {option.value === 'per_person' 
                                ? (isArabic ? 'يتم ضرب السعر في عدد الطلاب' : 'Price multiplied by number of students')
                                : (isArabic ? 'سعر ثابت للمجموعة بأكملها' : 'Fixed price for the entire group')
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Face-to-Face Pricing Section */}
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
                {isArabic ? 'التسعير (حضوري)' : 'Face-to-Face Pricing'}
              </h3>
              
              {/* Face-to-Face Private Lesson Price */}
              <div className="form-group">
                <label>{t.perHourPrivate || 'Per hour (private)'} *</label>
                <CustomDropdown
                  name="pricing_private_face_to_face"
                  value={pricingPrivateFaceToFace}
                  onChange={(e) => {
                    setPricingPrivateFaceToFace(e.target.value);
                    if (errors.pricingPrivateFaceToFace) {
                      setErrors(prev => ({ ...prev, pricingPrivateFaceToFace: null }));
                    }
                  }}
                  options={priceDropdownOptions}
                  placeholder={isArabic ? 'اختر السعر' : 'Select price'}
                  error={errors.pricingPrivateFaceToFace}
                />
                {errors.pricingPrivateFaceToFace && <span className="error-message">{errors.pricingPrivateFaceToFace}</span>}
              </div>

              {/* Face-to-Face Group Lesson Price */}
              <div className="form-group">
                <label>{t.perHourGroup || 'Per hour (group)'} *</label>
                <CustomDropdown
                  name="pricing_group_face_to_face"
                  value={pricingGroupFaceToFace}
                  onChange={(e) => {
                    setPricingGroupFaceToFace(e.target.value);
                    if (errors.pricingGroupFaceToFace) {
                      setErrors(prev => ({ ...prev, pricingGroupFaceToFace: null }));
                    }
                  }}
                  options={priceDropdownOptions}
                  placeholder={isArabic ? 'اختر السعر' : 'Select price'}
                  error={errors.pricingGroupFaceToFace}
                />
                {errors.pricingGroupFaceToFace && <span className="error-message">{errors.pricingGroupFaceToFace}</span>}
                
                {/* Group Pricing Model - Face-to-Face */}
                {pricingGroupFaceToFace && (
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ 
                      fontSize: '16px', 
                      fontWeight: 600, 
                      color: '#1f2937', 
                      marginBottom: '12px', 
                      display: 'block' 
                    }}>
                      {isArabic ? 'نموذج تسعير المجموعة (حضوري)' : 'Group Pricing Model (Face-to-Face)'} *
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px',
                      flexDirection: isArabic ? 'row-reverse' : 'row'
                    }}>
                      {groupPricingModelOptions.map((option) => {
                        const isSelected = groupPricingModelFaceToFace === option.value;
                        return (
                          <div
                            key={option.value}
                            onClick={() => setGroupPricingModelFaceToFace(option.value)}
                            style={{
                              flex: 1,
                              padding: '16px',
                              border: `2px solid ${isSelected ? '#ff7a1a' : '#e5e7eb'}`,
                              borderRadius: '12px',
                              background: isSelected ? '#fff4e6' : 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              textAlign: 'center'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#d1d5db';
                                e.currentTarget.style.background = '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.background = 'white';
                              }
                            }}
                          >
                            <div style={{
                              fontSize: '15px',
                              fontWeight: isSelected ? '600' : '500',
                              color: isSelected ? '#ff7a1a' : '#1f2937',
                              marginBottom: '4px'
                            }}>
                              {option.label}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              lineHeight: '1.4'
                            }}>
                              {option.value === 'per_person' 
                                ? (isArabic ? 'يتم ضرب السعر في عدد الطلاب' : 'Price multiplied by number of students')
                                : (isArabic ? 'سعر ثابت للمجموعة بأكملها' : 'Fixed price for the entire group')
                              }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Single Pricing Section (Online Only or Face-to-Face Only) */}
            {isOnlineOnly && (
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
                {isArabic ? 'التسعير (أونلاين)' : 'Online Pricing'}
              </h3>
            )}
            {isFaceToFaceOnly && (
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
                {isArabic ? 'التسعير (حضوري)' : 'Face-to-Face Pricing'}
              </h3>
            )}
            
            <div className="form-group">
              <label>{t.perHourPrivate || 'Per hour (private)'} *</label>
              <CustomDropdown
                name="pricing_private"
                value={pricingPrivate}
                onChange={(e) => {
                  setPricingPrivate(e.target.value);
                  if (errors.pricingPrivate) {
                    setErrors(prev => ({ ...prev, pricingPrivate: null }));
                  }
                }}
                options={priceDropdownOptions}
                placeholder={isArabic ? 'اختر السعر' : 'Select price'}
                error={errors.pricingPrivate}
              />
              {errors.pricingPrivate && <span className="error-message">{errors.pricingPrivate}</span>}
            </div>

            {/* Group Lesson Price */}
            <div className="form-group">
              <label>{t.perHourGroup || 'Per hour (group)'} *</label>
              <CustomDropdown
                name="pricing_group"
                value={pricingGroup}
                onChange={(e) => {
                  setPricingGroup(e.target.value);
                  if (errors.pricingGroup) {
                    setErrors(prev => ({ ...prev, pricingGroup: null }));
                  }
                }}
                options={priceDropdownOptions}
                placeholder={isArabic ? 'اختر السعر' : 'Select price'}
                error={errors.pricingGroup}
              />
              {errors.pricingGroup && <span className="error-message">{errors.pricingGroup}</span>}
              
              {/* Group Pricing Model - Show only when price is selected */}
              {pricingGroup && (
            <div style={{ marginTop: '16px' }}>
              <label style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: '#1f2937', 
                marginBottom: '12px', 
                display: 'block' 
              }}>
                {isArabic ? 'نموذج تسعير المجموعة' : 'Group Pricing Model'} *
              </label>
              <div style={{ 
                display: 'flex', 
                gap: '12px',
                flexDirection: isArabic ? 'row-reverse' : 'row'
              }}>
                {groupPricingModelOptions.map((option) => {
                  const isSelected = groupPricingModel === option.value;
                  return (
                    <div
                      key={option.value}
                      onClick={() => setGroupPricingModel(option.value)}
                      style={{
                        flex: 1,
                        padding: '16px',
                        border: `2px solid ${isSelected ? '#ff7a1a' : '#e5e7eb'}`,
                        borderRadius: '12px',
                        background: isSelected ? '#fff4e6' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{
                        fontSize: '15px',
                        fontWeight: isSelected ? '600' : '500',
                        color: isSelected ? '#ff7a1a' : '#1f2937',
                        marginBottom: '4px'
                      }}>
                        {option.label}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        lineHeight: '1.4'
                      }}>
                        {option.value === 'per_person' 
                          ? (isArabic ? 'يتم ضرب السعر في عدد الطلاب' : 'Price multiplied by number of students')
                          : (isArabic ? 'سعر ثابت للمجموعة بأكملها' : 'Fixed price for the entire group')
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
          </>
        )}

        {/* Banking Information */}
        <div style={{ 
          marginTop: '32px', 
          paddingTop: '24px', 
          borderTop: '1px solid #e5e7eb' 
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
            {t.bankingInfo || 'Banking Information'}*
          </h3>

          <div className="form-group">
            <label>{t.iban || 'IBAN'} *</label>
            <input
              type="text"
              value={iban}
              onChange={(e) => {
                setIban(e.target.value.toUpperCase().replace(/\s/g, ''));
                if (errors.iban) {
                  setErrors(prev => ({ ...prev, iban: null }));
                }
              }}
              placeholder="BH..."
              style={{ textTransform: 'uppercase' }}
              className={errors.iban ? 'error' : ''}
            />
            <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {t.forReceivingPayments || 'For receiving payments'}
            </span>
            {errors.iban && <span className="error-message">{errors.iban}</span>}
          </div>

          <div className="form-group">
            <label>{t.bankName || 'Bank Name'} *</label>
            <CustomDropdown
              name="bank_name"
              value={bankName}
              onChange={(e) => {
                setBankName(e.target.value);
                // Clear otherBankName if a different bank is selected
                if (e.target.value !== 'Other' && e.target.value !== 'أخرى') {
                  setOtherBankName('');
                }
                if (errors.bankName) {
                  setErrors(prev => ({ ...prev, bankName: null, otherBankName: null }));
                }
              }}
              options={bankOptions}
              placeholder={isArabic ? 'اختر البنك' : 'Select bank'}
              error={errors.bankName || errors.otherBankName}
            />
            {(bankName === 'Other' || bankName === 'أخرى') && (
              <div style={{ marginTop: '12px' }}>
                <input
                  type="text"
                  value={otherBankName}
                  onChange={(e) => {
                    setOtherBankName(e.target.value);
                    if (errors.otherBankName) {
                      setErrors(prev => ({ ...prev, otherBankName: null }));
                    }
                  }}
                  placeholder={isArabic ? 'أدخل اسم البنك' : 'Enter bank name'}
                  className={errors.otherBankName ? 'error' : ''}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: `2px solid ${errors.otherBankName ? '#dc2626' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => {
                    if (!errors.otherBankName) {
                      e.target.style.borderColor = '#ff7a1a';
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.otherBankName) {
                      e.target.style.borderColor = '#e5e7eb';
                    }
                  }}
                />
                {errors.otherBankName && <span className="error-message">{errors.otherBankName}</span>}
              </div>
            )}
            {errors.bankName && !errors.otherBankName && <span className="error-message">{errors.bankName}</span>}
          </div>

          <div className="form-group">
            <label>{t.accountHolderName || 'Account Holder Name'} *</label>
            <input
              type="text"
              value={accountHolderName}
              onChange={(e) => {
                setAccountHolderName(e.target.value);
                if (errors.accountHolderName) {
                  setErrors(prev => ({ ...prev, accountHolderName: null }));
                }
              }}
              placeholder={isArabic ? 'الاسم كما يظهر في الحساب المصرفي' : 'Name as it appears on bank account'}
              className={errors.accountHolderName ? 'error' : ''}
            />
            {errors.accountHolderName && <span className="error-message">{errors.accountHolderName}</span>}
          </div>
        </div>

        {/* Actions */}
        <div className="wizard-actions">
          <button type="button" className="btn btn-secondary" onClick={() => {
            const finalBankName = (bankName === 'Other' || bankName === 'أخرى') ? otherBankName : bankName;
            const backData = isBoth ? {
              pricing_private_online_per_hour: pricingPrivateOnline,
              pricing_group_online_per_hour: pricingGroupOnline,
              pricing_private_face_to_face_per_hour: pricingPrivateFaceToFace,
              pricing_group_face_to_face_per_hour: pricingGroupFaceToFace,
              group_pricing_model_online: groupPricingModelOnline,
              group_pricing_model_face_to_face: groupPricingModelFaceToFace,
              iban: iban,
              bank_name: finalBankName,
              account_holder_name: accountHolderName
            } : {
              pricing_private_per_hour: pricingPrivate,
              pricing_group_per_hour: pricingGroup,
              group_pricing_model: groupPricingModel,
              iban: iban,
              bank_name: finalBankName,
              account_holder_name: accountHolderName
            };
            onBack(backData);
          }}>
            {t.back || 'Back'}
          </button>
          <button type="submit" className="btn btn-primary">
            {t.saveAndContinue || 'Save and continue'}
          </button>
        </div>
      </form>
    </div>
  );
}